import { createApi } from "@reduxjs/toolkit/dist/query/react"
import {
  RATE_CARDS_CONFIG_ENDPOINT,
  RATE_CARDS_CONFIGS_ENDPOINT,
  RTK_SLICE_API_LIST
} from "../../common/constants"
import { baseQuery, getTags } from "../api"
import { getQueryString } from "../../common/utils"
import {
  RateCardsConfig,
  RateCardsConfigListItemResponse,
  RateCardsConfigResponse,
  RateCardsConfigsListResponse,
  RateCardsConfigUpsertPayload,
  ScenarioPreferenceMapResponse,
  ScenarioPreferenceUpdatePayload
} from "../../types"
import bbApi from "../../api"

const reducerPath = "rateCardsConfig"
const rateCardsPath = "rate-cards"

const toRateCardsConfig = (
  item: RateCardsConfigListItemResponse
): RateCardsConfig => ({
  id: item.id,
  name: item.name,
  brand_id: Number(item.brand_id),
  is_default: item.is_default
})

export const rateCardsApi = createApi({
  reducerPath,
  baseQuery,
  tagTypes: [rateCardsPath],
  endpoints: builder => ({
    fetchRateCardsConfigs: builder.query({
      query: (params: { brand_id?: string }) =>
        `${RATE_CARDS_CONFIGS_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (
        responseData:
          | RateCardsConfigsListResponse
          | RateCardsConfigListItemResponse[]
      ) => {
        const configs = Array.isArray(responseData)
          ? responseData
          : responseData?.rate_cards_configs ?? []
        return configs.map(toRateCardsConfig)
      },
      providesTags: result =>
        Array.isArray(result) ? getTags(rateCardsPath, "id", result) : []
    }),
    fetchRateCardsConfig: builder.query({
      query: (params: { id: string }) =>
        `${RATE_CARDS_CONFIG_ENDPOINT}${getQueryString(params)}`,
      transformResponse: (responseData: RateCardsConfigResponse) => ({
        ...responseData,
        brand_id: Number(responseData.brand_id)
      }),
      providesTags: (result, error, arg) => [
        { type: rateCardsPath, id: arg.id }
      ]
    }),
    fetchDefaultRateCardsConfig: builder.query({
      query: () => `${RATE_CARDS_CONFIG_ENDPOINT}?id=`,
      transformResponse: (responseData: Record<string, unknown>) =>
        responseData ?? {}
    }),
    updateOrCreateRateCardsConfig: builder.mutation({
      queryFn: async (payload: RateCardsConfigUpsertPayload) => {
        if (!payload.id) {
          delete payload.id
        }
        const data = await bbApi.rateCards.updateRateCardsConfig(payload)
        return { data }
      },
      invalidatesTags: (result, error, arg) => {
        const resultData = result as
          | { status?: string; message?: string }
          | undefined
        const isErrorStatus = resultData?.status === "error"
        if (error || isErrorStatus) {
          return []
        }
        return [
          { type: rateCardsPath, id: arg.id },
          { type: rateCardsPath, id: RTK_SLICE_API_LIST }
        ]
      }
    }),
    deleteRateCardsConfig: builder.mutation({
      queryFn: async (id: string) => {
        const data = await bbApi.rateCards.deleteRateCardsConfig(id)
        return { data }
      },
      invalidatesTags: () => [{ type: rateCardsPath, id: RTK_SLICE_API_LIST }]
    }),
    fetchProfilePreferenceMap: builder.query<
      ScenarioPreferenceMapResponse,
      { brand_id: string }
    >({
      queryFn: async ({ brand_id }) => {
        const data = await bbApi.rateCards.getProfilePreferenceMap(brand_id)
        return { data }
      },
      providesTags: (_result, _error, arg) => [
        { type: rateCardsPath, id: `pref-map-${arg.brand_id}` }
      ]
    }),
    updateProfilePreferenceMap: builder.mutation<
      { status: string; message?: string },
      ScenarioPreferenceUpdatePayload
    >({
      queryFn: async payload => {
        const data = await bbApi.rateCards.updateProfilePreferenceMap(payload)
        return { data }
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: rateCardsPath, id: `pref-map-${arg.brand_id}` }
      ]
    })
  })
})

export const {
  useFetchRateCardsConfigsQuery,
  useFetchRateCardsConfigQuery,
  useFetchDefaultRateCardsConfigQuery,
  useUpdateOrCreateRateCardsConfigMutation,
  useDeleteRateCardsConfigMutation,
  useFetchProfilePreferenceMapQuery,
  useUpdateProfilePreferenceMapMutation
} = rateCardsApi
